// Main entry point for Garden Viewer 3D

import { auth } from '@/api'
import { debug } from '@/utils/debug'
import { initLocale, t, translatePage } from '@/i18n'
import { GardenViewer } from './GardenViewer'

// DOM Elements
const authPanel = document.getElementById('auth-panel')!
const loginBtn = document.getElementById('login-btn')!
const loadingOverlay = document.getElementById('loading-overlay')!
const loadingStatus = document.getElementById('loading-status')!
const canvasContainer = document.getElementById('canvas-container')!
const infoPanel = document.getElementById('info-panel')!
const gardenNameEl = document.getElementById('garden-name')!
const plantCountEl = document.getElementById('plant-count')!
const buildingCountEl = document.getElementById('building-count')!
const controlsHint = document.getElementById('controls-hint')!
const togglePanel = document.getElementById('toggle-panel')!
const viewToggle = document.getElementById('view-toggle')!
const walkToggle = document.getElementById('walk-toggle')!
const walkRow = document.getElementById('walk-row')!
const progressBar = document.getElementById('progress-bar')!
const progressText = document.getElementById('progress-text')!
const layerPlantsBtn = document.getElementById('layer-plants-btn')!
const layerBuildingsBtn = document.getElementById('layer-buildings-btn')!
const layerGroundBtn = document.getElementById('layer-ground-btn')!
const layerMetadataBtn = document.getElementById('layer-metadata-btn')!
const layerPanel = document.getElementById('layer-panel')!

let viewer: GardenViewer | null = null
let isStartingViewer = false

// Stage-based progress mapping (status key → percentage)
const PROGRESS_STAGES: Array<{ key: string; progress: number }> = [
  { key: 'status.loading', progress: 0 },
  { key: 'status.initializingViewer', progress: 5 },
  { key: 'status.loadingGardens', progress: 10 },
  { key: 'status.loadingGarden', progress: 15 },
  { key: 'status.loadingCategories', progress: 25 },
  { key: 'status.loadingAssets', progress: 35 },
  { key: 'status.preloadingModels', progress: 40 },
  { key: 'status.renderingScheme', progress: 45 },
  { key: 'status.renderingGround', progress: 55 },
  { key: 'status.renderingBuildings', progress: 70 },
  { key: 'status.renderingPlants', progress: 85 },
  { key: 'status.gardenLoaded', progress: 100 }
]

// Build lookup map from translated strings to progress values
function buildProgressLookup(): Map<string, number> {
  const lookup = new Map<string, number>()
  for (const stage of PROGRESS_STAGES) {
    lookup.set(t(stage.key), stage.progress)
  }
  return lookup
}

let progressLookup: Map<string, number> | null = null

function getProgressForStatus(statusText: string): number | undefined {
  // Build lookup lazily (after i18n is initialized)
  if (!progressLookup) {
    progressLookup = buildProgressLookup()
  }
  return progressLookup.get(statusText)
}

// Initialize the application
async function init() {
  // Initialize i18n before anything else
  initLocale()
  translatePage()

  debug.log('App', 'Garden Viewer 3D initializing...')
  debug.log('App', `URL: ${window.location.href}`)

  // Check for OAuth callback
  const params = new URLSearchParams(window.location.search)
  if (params.has('code')) {
    debug.log('Auth', 'OAuth callback detected, exchanging code...')
    showLoading(t('status.completingSignIn'))
    const success = await auth.handleCallback()
    if (!success) {
      debug.error('Auth', 'OAuth callback failed')
      hideLoading()
      showAuth()
      return
    }

    // URL params are now restored - reinitialize locale and debug
    initLocale()
    translatePage()
    debug.reinitialize()
    // Rebuild progress lookup with new locale
    progressLookup = null

    debug.success('Auth', 'OAuth callback successful')
  }

  // Check existing session
  const authState = auth.getState()
  debug.log('Auth', 'Current auth state', authState)

  if (authState.isAuthenticated) {
    debug.success('Auth', 'User is authenticated')
    hideAuth()  // Hide auth panel immediately
    showLoading(t('status.loading'))
    await startViewer()
  } else {
    // Auto-start OAuth flow instead of showing Sign In button
    debug.log('Auth', 'User not authenticated, starting OAuth flow...')
    hideAuth()
    showLoading(t('status.redirectingToLogin'))
    auth.startLogin()
  }

  // Subscribe to auth changes
  auth.subscribe((state) => {
    debug.log('Auth', 'Auth state changed', state)
    if (state.isAuthenticated) {
      hideAuth()
      startViewer()
    } else {
      showAuth()
      if (viewer) {
        viewer.dispose()
        viewer = null
      }
    }
  })

  // Login button click
  loginBtn.addEventListener('click', () => {
    debug.log('Auth', 'Login button clicked, starting OAuth flow...')
    auth.startLogin()
  })

  // View toggle button click
  viewToggle.addEventListener('click', toggleViewMode)

  // Walk mode toggle button click
  walkToggle.addEventListener('click', toggleWalkMode)

  // Layer toggle button clicks
  layerPlantsBtn.addEventListener('click', () => toggleLayerVisibility('plants'))
  layerBuildingsBtn.addEventListener('click', () => toggleLayerVisibility('buildings'))
  layerGroundBtn.addEventListener('click', () => toggleLayerVisibility('ground'))
  layerMetadataBtn.addEventListener('click', () => toggleLayerVisibility('metadata'))

  // Keyboard shortcuts
  document.addEventListener('keydown', handleGlobalKeydown)
}

async function startViewer() {
  // Prevent concurrent starts
  if (isStartingViewer) {
    debug.log('Viewer', 'Viewer already starting, skipping...')
    return
  }
  if (viewer) {
    debug.log('Viewer', 'Viewer already exists, skipping...')
    return
  }

  isStartingViewer = true
  debug.log('Viewer', 'Starting viewer...')
  showLoading(t('status.initializingViewer'))

  // Create viewer
  viewer = new GardenViewer({
    container: canvasContainer,
    onStatusChange: (status) => {
      loadingStatus.textContent = status
      updateProgress(status)
    },
    onGardenLoaded: (garden) => {
      hideLoading()
      showUI()
      updateUI(garden)
    },
    onError: (error) => {
      loadingStatus.textContent = `${t('error.prefix')} ${error.message}`
    }
  })

  // Initialize and wait for it to complete
  debug.log('Viewer', 'Initializing viewer...')
  try {
    await viewer.initialize()
    debug.success('Viewer', 'Viewer initialized')

    // Set initial view mode from URL parameter (default: 2D)
    const initialCamera = getCameraFromUrl()
    viewer.setViewMode(initialCamera)
    debug.log('Viewer', `Initial view mode: ${initialCamera}`)

    // Listen for walk mode changes (e.g., when user presses Escape)
    viewer.setOnWalkModeChange(() => {
      updateWalkToggle()
    })
  } catch (error) {
    debug.error('Viewer', 'Failed to initialize', error)
    isStartingViewer = false
    viewer = null
    throw error
  }

  // Viewer is ready, allow future calls (they'll be blocked by viewer !== null check)
  isStartingViewer = false

  // Load garden from URL or use first available
  const gardenId = getGardenIdFromUrl()
  debug.log('Garden', `Garden ID from URL: ${gardenId || 'none'}`)

  if (gardenId) {
    await viewer.loadGarden(gardenId)
  } else {
    // Try to get user's first garden
    try {
      debug.log('API', 'No garden ID specified, fetching garden list...')
      showLoading(t('status.loadingGardens'))
      const { api } = await import('@/api')
      const gardens = await api.getGardens()

      debug.log('API', `Found ${gardens.length} gardens`, gardens)

      if (gardens.length > 0) {
        debug.log('Garden', `Loading first garden: ${gardens[0].id}`)
        await viewer.loadGarden(gardens[0].id)
      } else {
        debug.warn('Garden', 'No gardens found')
        hideLoading()
        loadingStatus.textContent = t('error.noGardens')
        loadingOverlay.classList.remove('hidden')
      }
    } catch (error) {
      debug.error('API', 'Failed to load gardens list', error)
      loadingStatus.textContent = t('error.loadFailed')
    }
  }
}

function getGardenIdFromUrl(): string | null {
  // Support formats: /gardens/{id} or ?garden={id}
  const pathMatch = window.location.pathname.match(/\/gardens\/([^/]+)/)
  if (pathMatch) {
    return pathMatch[1]
  }

  const params = new URLSearchParams(window.location.search)
  return params.get('garden')
}

function getCameraFromUrl(): '2d' | '3d' {
  const params = new URLSearchParams(window.location.search)
  const camera = params.get('camera')?.toLowerCase()
  // Default to 2D, only switch to 3D if explicitly requested
  return camera === '3d' ? '3d' : '2d'
}

function updateViewToggle(): void {
  if (!viewer) return
  const mode = viewer.getViewMode()
  const isWalking = viewer.isWalkMode()

  if (mode === '3d') {
    viewToggle.classList.add('active')
    // Show walk button only in 3D mode
    walkRow.style.display = 'flex'

    if (isWalking) {
      controlsHint.textContent = t('controls.walk')
    } else {
      controlsHint.textContent = t('controls.3d')
    }
  } else {
    viewToggle.classList.remove('active')
    // Hide walk button in 2D mode
    walkRow.style.display = 'none'
    controlsHint.textContent = t('controls.2d')
  }
}

function updateWalkToggle(): void {
  if (!viewer) return
  const isWalking = viewer.isWalkMode()

  if (isWalking) {
    walkToggle.classList.add('active')
  } else {
    walkToggle.classList.remove('active')
  }

  updateViewToggle() // Update hints
}

function toggleViewMode(): void {
  if (!viewer) return
  const currentMode = viewer.getViewMode()
  const newMode = currentMode === '2d' ? '3d' : '2d'
  viewer.setViewMode(newMode)
  updateViewToggle()
  updateWalkToggle()
  debug.log('App', `View mode toggled to: ${newMode}`)
}

function toggleWalkMode(): void {
  if (!viewer) return
  // Can only toggle walk mode in 3D view
  if (viewer.getViewMode() !== '3d') return

  const newWalkMode = !viewer.isWalkMode()
  viewer.setWalkMode(newWalkMode)
  updateWalkToggle()
  debug.log('App', `Walk mode toggled to: ${newWalkMode}`)
}

function toggleLayerVisibility(layer: 'plants' | 'buildings' | 'ground' | 'metadata'): void {
  if (!viewer) return
  const newState = viewer.toggleLayer(layer)
  updateLayerButtons()
  debug.log('App', `Layer '${layer}' toggled to: ${newState}`)
}

function updateLayerButtons(): void {
  if (!viewer) return
  const visibility = viewer.getLayerVisibility()

  // Update button active state based on layer visibility
  layerPlantsBtn.classList.toggle('active', visibility.plants)
  layerBuildingsBtn.classList.toggle('active', visibility.buildings)
  layerGroundBtn.classList.toggle('active', visibility.ground)
  layerMetadataBtn.classList.toggle('active', visibility.metadata)
}

function handleGlobalKeydown(event: KeyboardEvent): void {
  // Ignore if typing in an input
  if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
    return
  }

  // Skip if modifier keys are held (except Shift)
  if (event.ctrlKey || event.metaKey || event.altKey) {
    return
  }

  // F key toggles walk mode (only in 3D)
  if (event.code === 'KeyF') {
    if (viewer?.getViewMode() === '3d') {
      toggleWalkMode()
      event.preventDefault()
    }
    return
  }

  // Layer visibility toggles: 1, 2, 3, 4
  switch (event.key) {
    case '1':
      toggleLayerVisibility('plants')
      event.preventDefault()
      break
    case '2':
      toggleLayerVisibility('buildings')
      event.preventDefault()
      break
    case '3':
      toggleLayerVisibility('ground')
      event.preventDefault()
      break
    case '4':
      toggleLayerVisibility('metadata')
      event.preventDefault()
      break
  }
}

function showAuth() {
  authPanel.classList.remove('hidden')
  hideLoading()
}

function hideAuth() {
  authPanel.classList.add('hidden')
}

function showLoading(message: string = 'Loading...') {
  loadingStatus.textContent = message
  loadingOverlay.classList.remove('hidden')
  // Update progress based on the message
  updateProgress(message)
}

function hideLoading() {
  loadingOverlay.classList.add('hidden')
}

function updateProgress(statusText: string) {
  const progress = getProgressForStatus(statusText)
  if (progress !== undefined) {
    progressBar.style.width = `${progress}%`
    progressText.textContent = `${progress}%`
  }
  // Unknown status: keep previous progress (don't update)
}

function showUI() {
  infoPanel.style.display = 'block'
  controlsHint.style.display = 'block'
  togglePanel.style.display = 'block'
  layerPanel.style.display = 'flex'
  updateViewToggle()
  updateLayerButtons()
}

function updateUI(garden: import('@/types').Garden) {
  gardenNameEl.textContent = garden.name || t('ui.defaultGardenName')

  if (viewer) {
    const stats = viewer.getStats()
    plantCountEl.textContent = stats.plants.toString()
    buildingCountEl.textContent = stats.buildings.toString()
  }
}

// Start the app
init().catch((error) => {
  debug.error('App', 'Fatal error during initialization', error)
  console.error('Fatal error:', error)
})
